import classnames from "classnames";

import {CheckStepDef} from "@/lib/types/checks.ts";
import {TxnResult} from "@/lib/types/transaction.ts";
import {formatDescription} from "@/pages/alerting/components/details/rca/legacyImports.ts";

interface IfSubStepResult {
  step_def: string;
  title?: string;
  values?: object;
  passed?: boolean;
  error?: string;
}

interface IfStepResult {
  conditions_passed?: boolean;
  condition_results?: IfSubStepResult[];
  branch_executed?: string;
  branch_results?: IfSubStepResult[];
}

interface RCAIfBranchesProps {
  result: TxnResult;
  stepsDef: CheckStepDef[];
}

const describe = (subStep: IfSubStepResult, stepsDef: CheckStepDef[]): string | null => {
  const stepDefTuple = stepsDef.find((step) => step[0] === subStep.step_def);
  if (!stepDefTuple) {
    return null;
  }
  return formatDescription(stepDefTuple[1], subStep as unknown as TxnResult);
};

/* Only executed sub-steps reach the results, so an entry without an error is one that passed. */
const SubStepList = ({
  label,
  subSteps,
  stepsDef,
}: {
  label: string;
  subSteps: IfSubStepResult[];
  stepsDef: CheckStepDef[];
}) => {
  if (!subSteps.length) {
    return null;
  }
  return (
    <>
      <div className="text-muted small mt-1">{label}</div>
      <ol className="list-unstyled ms-3 mb-0">
        {subSteps.map((subStep, idx) => {
          const desc = describe(subStep, stepsDef);
          return (
            <li
              key={idx}
              className={classnames({
                "text-danger": !!subStep.error,
                "text-success": !subStep.error,
              })}
            >
              <span className="me-2 text-muted">{idx + 1}.</span>
              {desc ? <span className="step" dangerouslySetInnerHTML={{__html: desc}} /> : subStep.step_def}
            </li>
          );
        })}
      </ol>
    </>
  );
};

/*
 * Steps nested in an IF step produce no top-level result of their own, so without this the
 * outcome of every condition and branch step is invisible in the root cause analysis.
 */
export const RCAIfBranches = ({result, stepsDef}: RCAIfBranchesProps) => {
  const ifResult = result.result as IfStepResult | null;
  if (!ifResult || (!ifResult.condition_results?.length && !ifResult.branch_results?.length)) {
    return null;
  }

  return (
    <div className="details-panel ms-4">
      <SubStepList label="Conditions" subSteps={ifResult.condition_results || []} stepsDef={stepsDef} />
      <SubStepList
        label={`${ifResult.branch_executed || ""} branch`.trim()}
        subSteps={ifResult.branch_results || []}
        stepsDef={stepsDef}
      />
    </div>
  );
};
